import { forwardRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Slide from "@mui/material/Slide";
import { alpha, useTheme } from "@mui/material/styles";
import { IoClose } from "react-icons/io5";
import { BRAND, BRAND_DARK } from "../MobileList";
import Permission from "../userPermission/userPermission";


// The brand header colour comes from the theme (MuiDialogTitle in
// src/themes/overrides.js); the grey body and white footer are set here so
// they only apply to FormDialog.

const footerSx = {
  py: 1.75,
  bgcolor: "#fff",
  borderTop: "1px solid #eaecf0",
  gap: 1.5,
  "& > :not(:first-of-type)": { ml: 0, mr: 0 },
};

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const panelSx = {
  bgcolor: "#ffffff",
  border: "1px solid #edf0f3",
  borderRadius: 3,
  boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
};

// A titled, bordered group of fields. Children are Grid items
// (xs={12} sm={6} for half width, xs={12} for full width).
export const SectionCard = ({ icon, title, children }) => (
  <Box sx={{ ...panelSx, p: { xs: 2, sm: 2.5 } }}>
    {title && (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
        {icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(BRAND, 0.12),
              color: BRAND,
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#1f2a37" }}>
          {title}
        </Typography>
      </Stack>
    )}
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

// A plain white panel for content that is not a field grid (tables, lists...).
export const PlainCard = ({ children, sx, ...rest }) => (
  <Box {...rest} sx={{ ...panelSx, p: { xs: 1.5, sm: 2 }, ...sx }}>
    {children}
  </Box>
);

// A boolean field as a full-width toggle row. Pass the react-hook-form `field`.
export const SwitchCard = ({ field, label, onChange }) => (
  <Box
    sx={{
      border: "1px solid #edf0f3",
      borderRadius: 2,
      px: 2,
      py: 0.25,
      bgcolor: "#fbfcfd",
      height: "100%",
      display: "flex",
      alignItems: "center",
      transition: "border-color .15s ease",
      "&:hover": { borderColor: alpha(BRAND, 0.5) },
    }}
  >
    <FormControlLabel
      control={
        <Switch
          {...field}
          checked={!!field.value}
          onChange={onChange ?? field.onChange}
        />
      }
      label={label}
      labelPlacement="start"
      sx={{
        ml: 0,
        mr: 0,
        width: "100%",
        justifyContent: "space-between",
        "& .MuiFormControlLabel-label": { fontSize: 14, color: "#344054" },
      }}
    />
  </Box>
);

/**
 * Shared add/edit dialog: brand header, grey body of SectionCards and a
 * footer with cancel/save. Full screen on phones/tablets.
 *
 * Pass `onSubmit` to wrap the body in a <form> with a save button.
 * Omit it for read-only dialogs (the footer then only shows the close button).
 * `saveColor="error"` for destructive saves (cancel, delete...).
 * `savePermission` ({ permission, role }) hides the save button for users
 * without access. `loading` shows a progress bar under the header.
 * `keepMounted={false}` for heavy content (maps, data that loads on mount) so
 * it only renders while open. `noValidate` skips the browser's `required`
 * checks when react-hook-form validates instead.
 */
const FormDialog = ({
  open,
  onClose,
  icon,
  title,
  subtitle,
  maxWidth = "sm",
  onSubmit,
  saving = false,
  saveDisabled = false,
  saveText = "حفظ",
  saveColor = "primary",
  cancelText = "إلغاء",
  closeText = "اغلاق",
  savePermission,
  extraActions,
  hideFooter = false,
  loading = false,
  keepMounted = true,
  noValidate = false,
  children,
}) => {
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const saveButton = onSubmit && (
    <LoadingButton
      type="submit"
      variant="contained"
      loading={saving}
      disabled={saveDisabled}
      color={saveColor}
      fullWidth={smallScreen}
      sx={{
        px: 4,
        ...(saveColor === "primary" && {
          boxShadow: `0 4px 12px ${alpha(BRAND, 0.3)}`,
          "&:hover": { bgcolor: BRAND_DARK },
        }),
      }}
    >
      {saveText}
    </LoadingButton>
  );

  const footer = hideFooter ? null : (
    <DialogActions sx={{ ...footerSx, px: { xs: 2, sm: 3 } }}>
      {extraActions}
      <Button
        onClick={onClose}
        variant="outlined"
        fullWidth={smallScreen && !!onSubmit}
        sx={{
          color: "#475467",
          borderColor: "#d0d5dd",
          px: 3,
          "&:hover": { borderColor: "#98a2b3", bgcolor: "#f9fafb" },
        }}
      >
        {onSubmit ? cancelText : closeText}
      </Button>
      {savePermission ? (
        <Permission {...savePermission}>{saveButton}</Permission>
      ) : (
        saveButton
      )}
    </DialogActions>
  );

  const body = (
    <>
      <DialogContent sx={{ bgcolor: "#f4f6f8", p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2.5}>{children}</Stack>
      </DialogContent>
      {footer}
    </>
  );

  const bodyStyle = {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  };

  return (
    <Dialog
      open={!!open}
      TransitionComponent={Transition}
      keepMounted={keepMounted}
      fullWidth
      fullScreen={smallScreen}
      maxWidth={maxWidth}
      dir="rtl"
      onClose={onClose}
      aria-describedby="alert-dialog-slide-description"
      PaperProps={{
        sx: { maxHeight: smallScreen ? "100%" : "94vh", overflow: "hidden" },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.75, sm: 2.25 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          {icon && (
            <Box
              sx={{
                width: { xs: 40, sm: 46 },
                height: { xs: 40, sm: 46 },
                borderRadius: "14px",
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(255,255,255,0.18)",
                fontSize: { xs: 22, sm: 26 },
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontWeight: 700, fontSize: { xs: 17, sm: 20 }, lineHeight: 1.2 }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                sx={{ opacity: 0.82, fontSize: { xs: 12, sm: 13 }, mt: 0.25 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          aria-label="اغلاق"
          sx={{
            color: "#fff",
            flexShrink: 0,
            "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: "#fff" },
          }}
        >
          <IoClose />
        </IconButton>
      </DialogTitle>
      {loading && <LinearProgress sx={{ flexShrink: 0 }} />}

      {onSubmit ? (
        <form
          // React events bubble through the portal, so a dialog opened from
          // inside another form would also submit that form.
          onSubmit={(e) => {
            e.stopPropagation();
            onSubmit(e);
          }}
          noValidate={noValidate}
          style={bodyStyle}
        >
          {body}
        </form>
      ) : (
        <Box sx={bodyStyle}>{body}</Box>
      )}
    </Dialog>
  );
};

export default FormDialog;
